<template>
  <button
    v-if="variablesSyncState !== 'success'"
    class="wf-main-button"
    @click="handleVariablesButtonClick"
    :disabled="!isSyncEnabled"
  >
    <span v-if="!isLoading">Sync to Webflow</span>
    <template v-else>
      <span class="small-loader"></span>
      <span>Syncing variables...</span>
    </template>
  </button>
  <template v-else>
    <button class="wf-main-button wf-main-button--secondary" @click="handleBackClick">
      <WfIcon icon="back-arrow" />
      Back
    </button>
    <WfOpenInWebflowButton />
  </template>
</template>

<script setup>
import { log } from '@/helpers/logger';
import { errorHandling } from '@/helpers/errors';
import { useStore } from 'vuex';
import { computed } from 'vue';
import { selectOrDeselectAllVariables } from '@/helpers/variables';
import { createExportEventObject } from '@/helpers/analytics';
import { trackEvent, FIGMA_EXPORT_COMPLETED } from '@/helpers/analytics';
import { createSyncData } from '@/api/sync-api';

import WfIcon from '@/components/WfIcon.vue';
import WfOpenInWebflowButton from './../WfOpenInWebflowButton.vue';

const store = useStore();

// Computed properties
const isLoading = computed(() => store.state.isLoading);
const variablesSyncState = computed(() => store.state.variablesSyncState);
const selectedSiteId = computed(() => store.state.selectedSiteId);
const figmaFileName = computed(() => store.state.figmaFileName);
const isSyncEnabled = computed(() => store.getters['variable/isSyncEnabled']);

// Methods
const setVariablesSyncState = (state) => store.commit('setVariablesSyncState', state);
const setIsLoading = (state) => store.commit('setIsLoading', state);

const handleVariablesButtonClick = async () => {
  setIsLoading(true);
  const startTime = performance.now();

  const collections = store.getters['variable/selectedCollections'].map(
    ({ id, name, libraryId, libraryName }) => ({
      id,
      name,
      libraryId,
      libraryName,
    })
  );
  const modes = store.getters['variable/selectedModes'];
  const groups = store.getters['variable/selectedGroups'];
  const variables = store.getters['variable/selectedVariables'].map(
    ({ id, name, type, groupId, collectionId, modes }) => {
      const group = groupId ? groups.find((group) => group.id === groupId) : null;
      return {
        id,
        collectionId,
        type,
        modes,
        name: group && group.name !== 'Root' ? `${group.name}/${name}` : name,
      };
    }
  );

  const payload = {
    variables,
    groups,
    modes,
    collections,
    figma_file_name: figmaFileName.value,
    source: 'variables',
  };

  let syncId;
  try {
    const response = await createSyncData(selectedSiteId.value, payload);
    syncId = response.sync_id;
  } catch (error) {
    log(error);
    errorHandling(error);
    setIsLoading(false);
    return;
  }

  setVariablesSyncState('success');
  const eventObject = createExportEventObject('variables', payload, startTime, syncId);
  trackEvent(FIGMA_EXPORT_COMPLETED, eventObject);
  setIsLoading(false);
  selectOrDeselectAllVariables('select');
};

const handleBackClick = () => {
  setVariablesSyncState('ready');
};
</script>
